import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import Button from '../components/ui/button';
import Badge from '../components/ui/badge';
import {
  BadgeCheck,
  CheckCircle,
  CheckSquare,
  BarChart3,
  ShieldCheck,
  Code,
  Users
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const Home = () => {
  const { user } = useAuth();

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5">
      {/* Hero Section */}
      <section className="relative py-20 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <Badge variant="secondary" className="mb-8 px-4 py-2">
              <BadgeCheck className="w-4 h-4 mr-2" />✓ GPT-4 ✓ Claude ✓ Gemini ✓
              LLaMA
            </Badge>
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-foreground mb-6 bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text">
              Check & Validate
              <br />
              <span className="bg-gradient-to-r from-primary to-green-600 bg-clip-text text-transparent">
                Every LLM
              </span>
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-12 leading-relaxed">
              Systematically validate, benchmark, and verify AI language models
              with comprehensive testing suites. Ensure quality, safety, and
              performance before deployment.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              {user ? (
                <Link to="/dashboard">
                  <Button
                    size="lg"
                    className="w-full sm:w-auto text-lg px-8 py-6"
                  >
                    <CheckCircle className="mr-2 w-5 h-5" />
                    Go to Dashboard
                  </Button>
                </Link>
              ) : (
                <Link to="/register">
                  <Button
                    size="lg"
                    className="w-full sm:w-auto text-lg px-8 py-6"
                  >
                    <CheckCircle className="mr-2 w-5 h-5" />
                    Start Checking Free
                  </Button>
                </Link>
              )}
              <Link to="/website-checks">
                <Button
                  variant="outline"
                  size="lg"
                  className="w-full sm:w-auto text-lg px-8 py-6"
                >
                  <BadgeCheck className="mr-2 w-5 h-5" />
                  View Website Checks Demo
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              Everything you need to validate LLMs
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Professional-grade validation tools for comprehensive LLM testing,
              verification, and quality assurance
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:border-green-200">
              <CardHeader>
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                  <CheckSquare className="w-6 h-6 text-green-600" />
                </div>
                <CardTitle>Multi-Model Validation</CardTitle>
                <CardDescription>
                  Validate and compare responses from GPT-4, Claude, Gemini, and
                  other leading models with comprehensive checkpoints
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:border-blue-200">
              <CardHeader>
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                  <BarChart3 className="w-6 h-6 text-blue-600" />
                </div>
                <CardTitle>Quality Metrics</CardTitle>
                <CardDescription>
                  Monitor accuracy, consistency, response quality, and
                  performance metrics with real-time validation scoring
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:border-purple-200">
              <CardHeader>
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                  <BadgeCheck className="w-6 h-6 text-purple-600" />
                </div>
                <CardTitle>Verification Benchmarks</CardTitle>
                <CardDescription>
                  Run standardized verification tests and create custom
                  validation suites for your specific requirements
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:border-red-200">
              <CardHeader>
                <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mb-4">
                  <ShieldCheck className="w-6 h-6 text-red-600" />
                </div>
                <CardTitle>Safety Validation</CardTitle>
                <CardDescription>
                  Comprehensive safety checks, bias detection, and ethical
                  validation with automated red-team testing
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:border-emerald-200">
              <CardHeader>
                <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center mb-4">
                  <Code className="w-6 h-6 text-emerald-600" />
                </div>
                <CardTitle>API Validation</CardTitle>
                <CardDescription>
                  Integrate validation checks directly into your CI/CD pipeline
                  with automated testing workflows
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:border-orange-200">
              <CardHeader>
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                  <Users className="w-6 h-6 text-orange-600" />
                </div>
                <CardTitle>Team Validation</CardTitle>
                <CardDescription>
                  Collaborate on validation results, share test suites, and
                  build verified knowledge bases with your team
                </CardDescription>
              </CardHeader>
            </Card>
          </div>
        </div>
      </section>

      {/* Models Section */}
      <section
        id="models"
        className="py-20 bg-gradient-to-br from-primary/5 via-green-50/50 to-background"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              Validate the latest LLMs
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Comprehensive validation support for cutting-edge language models
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                name: "GPT-4 Turbo",
                provider: "OpenAI",
                status: "✓ Verified",
                color: "green",
              },
              {
                name: "Claude 3 Opus",
                provider: "Anthropic",
                status: "✓ Verified",
                color: "green",
              },
              {
                name: "Gemini Pro",
                provider: "Google",
                status: "✓ Verified",
                color: "green",
              },
              {
                name: "LLaMA 2",
                provider: "Meta",
                status: "✓ Verified",
                color: "green",
              },
            ].map((model, index) => (
              <Card
                key={index}
                className="border-0 shadow-md hover:shadow-lg transition-all duration-300 hover:border-green-200"
              >
                <CardHeader className="pb-4">
                  <div className="flex items-center justify-between mb-2">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      {model.name}
                    </CardTitle>
                    <Badge
                      variant="secondary"
                      className="text-xs bg-green-100 text-green-700"
                    >
                      {model.status}
                    </Badge>
                  </div>
                  <CardDescription>{model.provider}</CardDescription>
                </CardHeader>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary/5">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6">
            Ready to validate your LLMs?
          </h2>
          <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
            Join thousands of developers and researchers using LLMChecks to
            ensure AI quality, safety, and reliability
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {user ? (
              <Link to="/dashboard">
                <Button
                  size="lg"
                  className="w-full sm:w-auto text-lg px-8 py-6 bg-green-600 hover:bg-green-700"
                >
                  <CheckCircle className="mr-2 w-5 h-5" />
                  Go to Dashboard
                </Button>
              </Link>
            ) : (
              <Link to="/register">
                <Button
                  size="lg"
                  className="w-full sm:w-auto text-lg px-8 py-6 bg-green-600 hover:bg-green-700"
                >
                  <CheckCircle className="mr-2 w-5 h-5" />
                  Start Validation
                </Button>
              </Link>
            )}
            <Link to="/website-checks">
              <Button
                variant="outline"
                size="lg"
                className="w-full sm:w-auto text-lg px-8 py-6 border-green-200 hover:bg-green-50"
              >
                <BadgeCheck className="mr-2 w-5 h-5" />
                View Website Checks Suite
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
